import { Calendar, CheckCircle2, ClipboardCheck } from "lucide-react";

/**
 * Kişisel programdaki ödevler (metadata.date / metadata.day) hafta kartında GÜN GÜN listelenir (19 Eki):
 * öğrenci kâğıttaki programla aynı sırayı görür; bugünün günü vurgulanır. Gün bilgisi olmayan (şubeye
 * gönderilen) ödevler eskisi gibi ders ders kalır.
 */
export interface HomeworkDayGroup {
  date: string;
  label: string;
  homeworks: any[];
}

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const statusOf = (hw: any) => {
  const overdue = hw.deadline && new Date() > new Date(hw.deadline) && ["PENDING", "STARTED"].includes(hw.status);
  if (overdue) return { label: "Süresi Geçti", cls: "bg-orange-100 text-orange-800 border-orange-200" };
  if (hw.status === "COMPLETED") return { label: "Tamamlandı", cls: "bg-green-100 text-green-800 border-green-200" };
  if (hw.status === "FAILED") return { label: "Başarısız", cls: "bg-red-100 text-red-800 border-red-200" };
  if (hw.status === "STARTED") return { label: "Devam Ediyor", cls: "bg-blue-100 text-blue-800 border-blue-200" };
  return { label: "Bekliyor", cls: "bg-yellow-100 text-yellow-800 border-yellow-200" };
};

const deadlineText = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "short" }) : "";

export default function HomeworkDayGroups({
  days,
  onOpen,
}: {
  days: Record<string, HomeworkDayGroup>;
  onOpen: (homework: any) => void;
}) {
  const groups = Object.values(days || {}).sort((a, b) => a.date.localeCompare(b.date));
  if (!groups.length) return null;
  const today = todayKey();

  return (
    <div className="space-y-3">
      {groups.map((day) => {
        const items = [...day.homeworks].sort((a, b) => (Number(a.metadata?.order) || 0) - (Number(b.metadata?.order) || 0));
        const done = items.filter((h) => h.status === "COMPLETED").length;
        const isToday = day.date === today;
        return (
          <div
            key={day.date}
            className={`rounded-lg border ${isToday ? "border-indigo-300 bg-indigo-50/40" : "border-gray-100"} p-2`}>
            <div className="mb-1.5 flex items-center gap-2 border-b border-gray-100 pb-1">
              <Calendar className={`h-4 w-4 ${isToday ? "text-indigo-600" : "text-purple-600"}`} />
              <h4 className="flex-1 text-sm font-semibold text-gray-900">
                {day.label}
                {isToday && (
                  <span className="ml-2 rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold text-white">Bugün</span>
                )}
              </h4>
              {items[0]?.deadline && (
                <span className="hidden text-[11px] text-gray-500 sm:inline">teslim {deadlineText(items[0].deadline)}</span>
              )}
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {done === items.length && items.length > 0 && <CheckCircle2 className="h-3 w-3 text-green-600" />}
                {done}/{items.length}
              </span>
            </div>
            <div className="space-y-1">
              {items.map((hw) => {
                const st = statusOf(hw);
                return (
                  <button
                    key={hw.id}
                    type="button"
                    onClick={() => onOpen(hw)}
                    className="group flex w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-2 text-left transition-colors hover:border-purple-300 hover:bg-gray-50">
                    <ClipboardCheck className="h-4 w-4 shrink-0 text-emerald-600" />
                    <span className="shrink-0 rounded bg-purple-50 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">
                      {hw.lesson?.title || "Ders"}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
                      {hw.lessonSection?.title || "(Bölüm Bulunamadı)"} ·{" "}
                      <b className="font-semibold">{hw.lessonSectionContent?.title || "Test"}</b>
                    </span>
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
